/**
 * `growth` — the portfolio ADS kit (canonical: listto). Contract + model.
 *
 * Responsibility separation (the part that prevents every app from reinventing it):
 *   - **`AdProvider`** (this layer, `provider.ts`) decides *whether it is ALLOWED*:
 *     environment flags, user tier, platform, and available network. It is the only layer
 *     that talks to an SDK.
 *   - **`adGate`** (`gate.ts`) decides *whether it SHOULD*: breakpoints,
 *     and minors' policy. PURE function, with no SDK or clock of its own.
 *   - **`AdCapsStore`** (`caps.ts`) keeps the counters **local-first**
 *     (`ads.cap.<placement>.<day>`): works offline and for guests.
 *
 * 100% domain-AGNOSTIC kit: it does not know what a list, habit, or
 * lesson is. Each app supplies its placement registry and rewards.
 *
 * **OFF by default**: without `ADS_ENABLED`/`VITE_ADS_ENABLED`, the entire layer is
 * inert and NO SDK is loaded (zero network requests—the privacy
 * promise is technical, not cosmetic).
 */

/** Supported formats, in the portfolio's order of preference. */
export const AD_KINDS = ["rewarded", "native", "interstitial", "banner"] as const;

export type AdKind = (typeof AD_KINDS)[number];

/** Points at which an ad is allowed after a natural break. */
export const AD_BREAKPOINTS = [
  "done-screen",
  "session-end",
  "between-screens",
  "stats",
  "feed-slot",
  "reward-moment",
] as const;

export type AdBreakpointId = (typeof AD_BREAKPOINTS)[number];

/** App ad tier (portfolio classification). */
export const AD_TIERS = ["A", "K", "C", "D"] as const;

/**
 * - `A` — consumer: all formats.
 * - `K` — child-directed: COPPA/Families regime (G banner + light interstitial; the
 *   rewarded exige puerta adulta).
 * - `C` — B2B/institucional: CERO ads.
 * - `D` — privacy: ZERO ads and zero bundled SDK.
 */
export type AdTier = (typeof AD_TIERS)[number];

/** Content ratings, **from strictest to most permissive** (the order is the rating). */
export const AD_CONTENT_RATINGS = ["G", "PG", "T", "MA"] as const;

export type AdContentRating = (typeof AD_CONTENT_RATINGS)[number];

/**
 * **Tier regime floor**: the minimum the classification guarantees,
 * regardless of configuration.
 *
 * It is a DECLARATION, not a preference: `resolveAdsConfig` applies it at the end
 * of resolution (`clampAdsConfigToTier`), so neither an environment
 * variable nor a back-office override can go below it. It can only be
 * endurecer.
 */
export interface AdTierRegime {
  /** `false` ⇒ ZERO ads and zero SDK, even if `ADS_ENABLED` says otherwise. */
  readonly adsAllowed: boolean;
  /** `true` ⇒ mandatory minors' regime (COPPA / Play Families). */
  readonly childDirected: boolean;
  /** Rating ceiling: nothing above it may be served in this tier. */
  readonly maxAdContentRating: AdContentRating;
}

/**
 * Strictest possible regime. This is applied to an UNKNOWN tier:
 * fail-closed—if someone adds a tier to `AD_TIERS` and forgets its regime, the
 * app runs without ads instead of serving them without policy.
 */
export const STRICTEST_AD_TIER_REGIME: AdTierRegime = {
  adsAllowed: false,
  childDirected: true,
  maxAdContentRating: "G",
};

/**
 * Declared regime for each tier (§9 of the ads strategy).
 *
 * - `A` — adult consumer: no special floor (the gate provides capping).
 * - `K` — children: ALWAYS child-directed and ALWAYS G-rated. This is the guarantee
 *   made to Google (Play Families) and the FTC (COPPA).
 * - `C`/`D` — B2B and privacy: zero ads, zero SDK.
 */
export const AD_TIER_REGIMES: Readonly<Record<AdTier, AdTierRegime>> = {
  A: { adsAllowed: true, childDirected: false, maxAdContentRating: "MA" },
  K: { adsAllowed: true, childDirected: true, maxAdContentRating: "G" },
  C: { adsAllowed: false, childDirected: false, maxAdContentRating: "G" },
  D: { adsAllowed: false, childDirected: false, maxAdContentRating: "G" },
};

/** Reward for a rewarded ad: what the user receives, visible BEFORE viewing it. */
export interface AdReward {
  /** Stable reward type ID (`streak-freeze`, `theme-unlock`, …). */
  type: string;
  amount: number;
  /** Lifetime of temporary rewards, in hours. Absent = consumable. */
  durationHours?: number;
}

/** Placement declaration in the app's typed registry. */
export interface PlacementDef {
  kind: AdKind;
  breakpoint: AdBreakpointId;
  /** For `rewarded` only: what is redeemed. */
  reward?: AdReward;
}

/** Placement registry for an app (`Record<id, PlacementDef>`). */
export type PlacementRegistry = Record<string, PlacementDef>;

/** Provider initialization result (including COPPA flags). */
export interface AdInitOptions {
  childDirected?: boolean;
  maxAdContentRating?: AdContentRating;
  testDeviceIds?: string[];
}

/** Ad display result. `reward` ONLY if the user completed it. */
export interface AdShowResult {
  shown: boolean;
  reward?: AdReward;
}

export type AdEventName = "impression" | "reward" | "dismiss" | "error";

export interface AdEvent {
  kind: AdKind;
  placementId: string;
  reward?: AdReward;
  error?: string;
}

/**
 * Ad provider contract. Implemented by one backend per network
 * (`none` = no-op and DEFAULT, `adsense` = web). The placement layer only talks
 * to this interface → changing networks does not touch a single screen.
 */
export interface AdProvider {
  /** Stable backend id (`none`, `adsense`, `admob`, …). */
  readonly id: string;
  /** Initializes the network. Idempotent. With `none`, does NOTHING (not even a request). */
  init(opts?: AdInitOptions): Promise<void>;
  /** `false` ⇒ the entire placement layer collapses for that format. */
  isEnabled(kind: AdKind): boolean;
  /** Best-effort preloading. `false` = no fill ⇒ the UI collapses, never a spinner. */
  prepare(kind: AdKind, placementId: string): Promise<boolean>;
  /** Displays the ad. For `rewarded`, resolves with the reward if completed. */
  show(kind: AdKind, placementId: string): Promise<AdShowResult>;
  /** Subscribes to events. Returns the unsubscribe function. */
  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void;
}

/** Resolved ads configuration (env → admin UI overrides). */
export interface AdsConfig {
  /** Master switch. **false by default**. */
  enabled: boolean;
  /** Provider backend. `none` by default (no-op, no SDK). */
  network: string;
  /** App ad tier (§9 of the strategy). */
  tier: AdTier;
  /** Minors' regime: forces the strict policy at the gate. */
  childDirected: boolean;
  /** Maximum content rating served (ceiling, never exceeded by the tier). */
  maxAdContentRating: AdContentRating;
  /** Web network client ID (e.g. `ca-pub-…`). Empty ⇒ no fill. */
  clientId: string;
}

/** Local day key `YYYY-MM-DD` (the caller determines the time zone). */
export type AdDayKey = string;

/**
 * Capping counters. They live **locally** (on the device) under
 * `ads.cap.<placement>.<day>` → work offline and without an account.
 */
export interface CapsState {
  /** Day to which the daily counters belong. */
  day: AdDayKey;
  /** Interstitials shown today (all placements). */
  interstitialsToday: number;
  /** Interstitials shown in the current session. */
  interstitialsThisSession: number;
  /** Epoch ms of the last INTERRUPTIVE ad shown. */
  lastInterstitialAt?: number;
  /** Rewards granted today, by `reward.type` (anti-farming). */
  rewardsToday: Record<string, number>;
  /** Rewards awarded today, by PLACEMENT (feeds backoff). */
  takenToday: Record<string, number>;
  /** Times the rewarded offer was shown today, by placement. */
  offersToday: Record<string, number>;
  /** Consecutive days with the offer shown and NO reward (backoff). */
  ignoredDays: Record<string, number>;
  /** Native ads already rendered on the current screen. */
  nativesOnScreen: number;
  /** Banners currently visible. */
  bannersVisible: number;
}

/** Signals for the current session (provided by the app-shell). */
export interface AdSessionState {
  /** Cumulative ACTIVE usage time in the session, in ms. */
  activeMs: number;
  /** Value units completed in the session (closed lists, …). */
  unitsCompleted: number;
  /** Is this the user's very first session? (clean onboarding). */
  isFirstEver: boolean;
}

export interface AdUserState {
  /** Ad-free tier (resolved by the monetization kit's `shouldShowAds`). */
  premium: boolean;
  /** Surface used by a minor ⇒ §6 regime. */
  child: boolean;
  /** Adult gate cleared (the only path to rewarded in the child regime). */
  adultGateCleared?: boolean;
}

export interface AdGateInput {
  kind: AdKind;
  placementId: string;
  /** Declared breakpoint for the placement (for minors' policy). */
  breakpoint?: AdBreakpointId;
  /** Reward type, `rewarded` only (per-type anti-farming). */
  rewardType?: string;
  /** Epoch ms. Injected: the gate is PURE. */
  now: number;
  caps: CapsState;
  session: AdSessionState;
  user: AdUserState;
  /** Active input / running timer / in-flight transaction ⇒ NEVER an ad. */
  inFlight: boolean;
  /** Numeric policy; defaults to the portfolio policy (§4). */
  policy?: Partial<AdCapPolicy>;
}

/** Stable reasons — feed telemetry and tests (not UI text). */
export type AdGateReason =
  | "allow"
  | "premium"
  | "in-flight"
  | "child-policy"
  | "first-session"
  | "no-value-unit"
  | "session-cap"
  | "daily-cap"
  | "min-gap"
  | "interruption-budget"
  | "reward-daily-cap"
  | "offer-daily-cap"
  | "offer-backoff"
  | "screen-cap";

export interface AdGateDecision {
  allow: boolean;
  reason: AdGateReason;
}

/** Numeric capping policy (portfolio defaults in `gate.ts`). */
export interface AdCapPolicy {
  interstitialPerSession: number;
  interstitialPerDay: number;
  /** Minimum time between interstitials, in usage ms. */
  minGapBetweenInterstitialsMs: number;
  /** Is the first interstitial allowed in the user's first-ever session? */
  allowInterstitialOnFirstSession: boolean;
  /** Value units the session must complete before an interstitial. */
  minUnitsCompleted: number;
  /** Maximum rewards per day PER TYPE (anti-farming). */
  rewardsPerDayPerType: number;
  /** Times an ignored rewarded offer is shown per day, by placement. */
  offersPerDayPerPlacement: number;
  /** Consecutive days of ignored offers before reducing their frequency. */
  ignoredDaysBeforeBackoff: number;
  /** Reduced frequency (offers/day) once in backoff. */
  offersPerDayWhenBackedOff: number;
  nativePerScreen: number;
  bannersVisible: number;
  /** Global budget: 1 interruptive impression per X ms of active usage. */
  interruptionBudgetMs: number;
}
